// The building blocks every page uses: a section heading with its kicker, a page hero, and the
// loading and error states of a read.
import clsx from "clsx";
import type { ReactNode } from "react";

export function SectionHead({ kicker, title, children, className }: { kicker: string; title: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={clsx("max-w-3xl", className)}>
      <p className="kicker">{kicker}</p>
      <h2 className="h2 mt-3">{title}</h2>
      {children ? <div className="lede mt-4">{children}</div> : null}
    </div>
  );
}

export function PageHero({ kicker, title, children, aside }: { kicker: string; title: ReactNode; children?: ReactNode; aside?: ReactNode }) {
  return (
    <section className="grain border-b border-line">
      <div className="wrap grid gap-10 py-14 sm:py-20 lg:grid-cols-[1.5fr_1fr] lg:items-end">
        <div>
          <p className="kicker">{kicker}</p>
          <h1 className="display mt-4 text-[2.9rem] sm:text-[4.2rem] lg:text-[5rem]">{title}</h1>
          {children ? <div className="lede mt-6 max-w-2xl">{children}</div> : null}
        </div>
        {aside ? <div>{aside}</div> : null}
      </div>
    </section>
  );
}

export function Loading({ label = "Loading" }: { label?: string }) {
  return (
    <div role="status" className="flex items-center gap-3 py-16 font-mono text-[12px] uppercase tracking-wider text-ink3">
      <span className="h-2 w-2 animate-blink rounded-full bg-signal" aria-hidden /> {label}…
    </div>
  );
}

export function Failed({ what, retry }: { what: string; retry?: () => void }) {
  return (
    <div role="alert" className="card my-10 p-6">
      <p className="font-semibold">We couldn't load {what}.</p>
      <p className="mt-1 text-ink2">The server may be restarting. Try again in a moment.</p>
      {retry ? (
        <button type="button" onClick={retry} className="btn-line mt-4">
          Try again
        </button>
      ) : null}
    </div>
  );
}
